import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent, type Editor, type JSONContent } from '@tiptap/react';
import { Selection } from '@tiptap/pm/state';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import type { TiptapDoc, RichEditorImageUploadHandler, RichEditorPreset } from './types';
import { ImageGrid, ImageGridCell } from './extensions/ImageGrid';
import { Callout } from './extensions/Callout';
import { Spacer } from './extensions/Spacer';
import { RichHighlight, RichTextAlign } from './extensions/textStyling';
import { RICH_BODY_ZOOMABLE_IMAGE_CLASS } from './richBodyClasses';
import { ALLOWED_IMAGE_MIME, isDocEmpty, uploadAndInsertImageGrid } from './parts/editorHelpers';
import { hasImageSegment, importPastedImages, splitHtmlByImages } from './parts/pastedImages';
import { Toolbar } from './parts/Toolbar';
import { PresetCards } from './parts/PresetCards';
import { PresetReplaceConfirmModal } from './parts/PresetReplaceConfirmModal';
// Deep import on purpose: the `chat` barrel drags ChatInput, emoji-mart,
// dompurify and the PDF viewer into every RichEditor consumer's bundle.
import { ImageLightbox } from '../chat/components/ImageLightbox';

export interface RichEditorProps {
    value: TiptapDoc;
    onChange: (doc: TiptapDoc) => void;
    /** Required unless `allowImages` is false: with images on, every insert path
     * (toolbar, paste, drop) needs somewhere to put the file. */
    onImageUpload?: RichEditorImageUploadHandler;
    /**
     * False turns the editor into a text-only one: no image button, and pasting
     * or dropping an image falls through to the default handler instead of
     * uploading. For fields whose column stores the document alone, with no
     * companion table to own uploaded files - a FAQ answer, a video
     * description - there is nowhere for an image to live.
     */
    allowImages?: boolean;
    /**
     * False leaves the caret where the user put it. The editor takes focus on
     * mount by default, which is right when it is the point of the screen (a
     * report body, a note) and wrong when it is one field among several - it
     * would pull the caret out of the field above it.
     */
    autoFocus?: boolean;
    placeholder?: string;
    chrome?: 'card' | 'flush' | 'bare';
    /** Card chrome defaults to 160. Flush chrome applies it only when passed -
     *  existing flush consumers rely on pure flex-fill sizing. */
    minHeightPx?: number;
    readOnly?: boolean;
    presets?: RichEditorPreset[];
    enableImageCaptions?: boolean;
    /** Extra content pinned to the toolbar's far right (e.g. a character count). Ignored in `readOnly`/`bare` mode, which never render a toolbar. */
    toolbarRightSlot?: React.ReactNode;
    /** Extra content rendered as its own row directly below the toolbar (e.g. quick-insert section chips). Ignored in `readOnly`/`bare` mode. */
    belowToolbarSlot?: React.ReactNode;
    /**
     * The callout, divider/spacer and alignment/highlight extensions are always
     * in the schema, so any stored document renders read-only on every surface.
     * These flags only reveal the toolbar controls that author them, keeping
     * Dx reports, patient notes and chat exactly as they are today.
     */
    allowCallouts?: boolean;
    allowLayoutBlocks?: boolean;
    allowTextStyling?: boolean;
}

/**
 * Bridges the optional `onImageUpload` to the upload helpers, which take a
 * required handler. Unreachable when `allowImages` is false - every call site is
 * behind an `imagesEnabledRef` check - so the throw is a wiring assertion, not a
 * user-facing path.
 */
function uploadOrThrow(
    handler: RichEditorImageUploadHandler | undefined,
    file: File
): Promise<{ url: string; fileId: string }> {
    if (!handler) {
        return Promise.reject(new Error('RichEditor: onImageUpload is required to insert images.'));
    }
    return handler(file);
}

function endsWithParagraph(content: unknown[]): boolean {
    return (content[content.length - 1] as { type?: string } | undefined)?.type === 'paragraph';
}

/**
 * Tiptap validates neither of the two things the editor then quietly repairs on
 * its next transaction: the schema needs `block+`, so `content: []` loads as a
 * childless doc, and StarterKit's TrailingNode wants a trailing paragraph. That
 * repair can land mid-focus and invalidate a transaction built beforehand,
 * which throws "Applying a mismatched transaction". Doing both up front leaves
 * nothing to repair. Read-only editors never dispatch, so they render the doc
 * exactly as stored rather than gaining a trailing blank line.
 */
function withBlockContent(doc: TiptapDoc, editable: boolean): TiptapDoc {
    const content = doc?.content?.length ? doc.content : [{ type: 'paragraph' }];
    const needsTrailing = editable && !endsWithParagraph(content);
    if (content === doc?.content && !needsTrailing) return doc;
    return { type: 'doc', content: needsTrailing ? [...content, { type: 'paragraph' }] : content };
}

/**
 * True when the editor already holds `next`. The incoming document is
 * round-tripped through the schema first: a document stored before an attribute
 * existed (paragraph alignment, say) omits it, while the editor always
 * serializes the full attribute set. Comparing the raw JSON would then never
 * match, re-setting the content - and moving the caret - on every render.
 */
/** Removes the markup the `allowTextStyling` extensions would otherwise adopt
 *  from a paste: `<mark>` unwraps to its text, and inline `text-align` is
 *  dropped while every other inline style is left alone. */
function stripTextStyling(html: string): string {
    try {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        parsed.body.querySelectorAll('mark').forEach((mark) => {
            mark.replaceWith(...Array.from(mark.childNodes));
        });
        parsed.body.querySelectorAll<HTMLElement>('[style]').forEach((element) => {
            element.style.removeProperty('text-align');
        });
        return parsed.body.innerHTML;
    } catch {
        // Never fail a paste over cosmetics.
        return html;
    }
}

function holdsSameDoc(editor: Editor, next: TiptapDoc): boolean {
    const current = JSON.stringify(editor.getJSON());
    // Typing round-trips a doc this editor just serialized, so the cheap
    // compare settles the common case; the parse below is only for documents
    // stored before an attribute gained a default.
    if (JSON.stringify(next) === current) return true;
    try {
        return JSON.stringify(ProseMirrorNode.fromJSON(editor.schema, next).toJSON()) === current;
    } catch {
        // A document the schema cannot parse is not the one we are holding.
        return false;
    }
}

/**
 * Always focus through this, never `editor.commands.focus()`: that builds its
 * transaction up front and dispatches it *after* focusing the DOM, and Safari
 * focuses synchronously, so a doc change in that window throws "Applying a
 * mismatched transaction". Dispatching the selection first leaves no window.
 */
function focusAtEnd(editor: Editor | null | undefined): void {
    if (!editor || editor.isDestroyed) return;
    const { state, view } = editor;
    const selection = Selection.atEnd(state.doc);
    // Skipped when the caret is already there: setSelection would clear the
    // stored marks, silently un-priming a toggled-on bold/italic.
    if (!state.selection.eq(selection)) view.dispatch(state.tr.setSelection(selection));
    // Deferred like tiptap's own focus command - focusing synchronously from a
    // React event handler misbehaves, and scrolling before focus measures a
    // stale layout.
    requestAnimationFrame(() => {
        if (editor.isDestroyed) return;
        editor.view.focus();
        editor.view.dispatch(editor.state.tr.scrollIntoView());
    });
}

export function RichEditor({
    value,
    onChange,
    onImageUpload,
    allowImages = true,
    autoFocus = true,
    placeholder,
    chrome = 'card',
    minHeightPx,
    readOnly = false,
    presets,
    enableImageCaptions = false,
    toolbarRightSlot,
    belowToolbarSlot,
    allowCallouts = false,
    allowLayoutBlocks = false,
    allowTextStyling = false
}: RichEditorProps) {
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    // Transient, auto-dismissing heads-up (e.g. "couldn't import a pasted image").
    const [notice, setNotice] = useState<string | null>(null);
    const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [presetsDismissed, setPresetsDismissed] = useState(false);
    // Set when a read-only reader clicks an image; drives the lightbox.
    const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);
    // Non-null when a preset pick was deferred for confirmation because the
    // editor already has content. Cleared on cancel or confirm.
    const [pendingPreset, setPendingPreset] = useState<RichEditorPreset | null>(null);

    const showPresets =
        !readOnly && !presetsDismissed && (presets?.length ?? 0) > 0 && isDocEmpty(value);

    const applyPreset = (preset: RichEditorPreset): void => {
        if (isDocEmpty(value)) {
            onChange(preset.doc);
            return;
        }
        // Defer to a styled confirm modal so the destructive overwrite is a
        // deliberate two-tap action.
        setPendingPreset(preset);
    };

    const handleConfirmReplace = (): void => {
        if (pendingPreset) onChange(pendingPreset.doc);
        setPendingPreset(null);
    };

    const flashNotice = useCallback((message: string): void => {
        setNotice(message);
        if (noticeTimer.current) clearTimeout(noticeTimer.current);
        noticeTimer.current = setTimeout(() => setNotice(null), 4500);
    }, []);

    useEffect(
        () => () => {
            if (noticeTimer.current) clearTimeout(noticeTimer.current);
        },
        []
    );

    const onImageUploadRef = useRef(onImageUpload);
    // Read inside the editor's paste/drop handlers, which are created once and
    // would otherwise capture the first render's value.
    const imagesEnabled = allowImages && onImageUpload != null;
    const imagesEnabledRef = useRef(imagesEnabled);
    const textStylingRef = useRef(allowTextStyling);
    // Stable handle to the editor for async callbacks (e.g. inserting pasted
    // images once their upload resolves), avoiding a stale-closure capture.
    const editorRef = useRef<Editor | null>(null);
    // Read inside the click handler, which is created once and would otherwise
    // capture the first render's value.

    const closeLightbox = useCallback(() => setLightboxImage(null), []);

    // Delegated on the wrapper rather than through ProseMirror's
    // `handleDOMEvents`: this covers imageGrid cells and plain `image` nodes
    // alike, and does not depend on prosemirror-view's internal dispatch.
    const handleReadOnlyImageClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>): void => {
            const target = event.target instanceof Element ? event.target : null;
            const image = target?.closest('img') ?? null;
            const src = image?.getAttribute('src');
            if (!image || !src) return;
            event.preventDefault();
            event.stopPropagation();
            setLightboxImage({ src, alt: image.getAttribute('alt') ?? '' });
        },
        []
    );

    const editor = useEditor({
        editable: !readOnly,
        onCreate({ editor: e }) {
            editorRef.current = e;
            const storage = e.storage as unknown as Record<string, unknown>;
            storage.onImageUpload = onImageUpload;
            storage.enableImageCaptions = enableImageCaptions;
        },
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                // StarterKit v3 bundles link and underline. Both are registered
                // again below — Link with options StarterKit's default lacks — and
                // two extensions of one name make Tiptap warn and keep only one.
                link: false,
                underline: false,
                // StarterKit ships the node but no styling, so a divider was
                // invisible everywhere. Classes rather than app CSS - see
                // richBodyClasses for why.
                horizontalRule: {
                    HTMLAttributes: { class: 'my-4 h-0 border-t border-gray-200' }
                }
            }),
            Underline,
            Image.extend({
                addAttributes() {
                    return {
                        ...this.parent?.(),
                        'data-file-id': {
                            default: null,
                            parseHTML: (element) => element.getAttribute('data-file-id'),
                            renderHTML: (attrs) => {
                                const id = (attrs as { 'data-file-id'?: string | null })[
                                    'data-file-id'
                                ];
                                return id ? { 'data-file-id': id } : {};
                            }
                        }
                    };
                }
            }).configure({ inline: false, allowBase64: false }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' }
            }),
            Placeholder.configure({
                placeholder: placeholder ?? 'Type here…'
            }),
            ImageGrid,
            ImageGridCell,
            Callout,
            Spacer,
            RichTextAlign,
            RichHighlight
        ],
        content: withBlockContent(value, !readOnly) as unknown as JSONContent,
        onUpdate: ({ editor }) => {
            onChange(editor.getJSON() as TiptapDoc);
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm max-w-none focus:outline-none px-3 py-2.5 text-gray-800'
            },
            // Hiding the toolbar does not gate the clipboard: TipTap parses
            // pasted HTML against the whole schema, so a highlight or a centred
            // paragraph copied out of Gmail or Word would land on a surface with
            // no control to undo it. Strip both where the surface opts out.
            transformPastedHTML: (html) => {
                if (textStylingRef.current) return html;
                return stripTextStyling(html);
            },
            handlePaste: (view, event) => {
                if (!imagesEnabledRef.current) return false;
                const fromFiles = event.clipboardData?.files
                    ? Array.from(event.clipboardData.files)
                    : [];
                const fromItems = event.clipboardData?.items
                    ? Array.from(event.clipboardData.items)
                          .filter((it) => it.kind === 'file')
                          .map((it) => it.getAsFile())
                          .filter((f): f is File => f !== null)
                    : [];
                const dedup = new Map<string, File>();
                for (const f of [...fromFiles, ...fromItems]) {
                    dedup.set(`${f.name}:${f.size}:${f.type}`, f);
                }
                const imageFiles = [...dedup.values()].filter((f) =>
                    (ALLOWED_IMAGE_MIME as readonly string[]).includes(f.type)
                );
                if (imageFiles.length > 0) {
                    event.preventDefault();
                    void uploadAndInsertImageGrid(
                        view,
                        imageFiles,
                        (file) => uploadOrThrow(onImageUploadRef.current, file),
                        setUploadError,
                        setIsUploading
                    );
                    return true;
                }
                // No image files on the clipboard. If the rich-text payload embeds
                // images (e.g. pasting from Google Docs), split it into ordered
                // text/image segments and re-insert them in their original order once
                // the images finish uploading - preserving "text → image → text".
                // Stripped here as well as in `transformPastedHTML`: this branch
                // preventDefaults and builds nodes through `generateJSON`, so the
                // ProseMirror transform never runs and styling would slip past on
                // any paste that happens to carry an image.
                const rawHtml = event.clipboardData?.getData('text/html') ?? '';
                const html = textStylingRef.current ? rawHtml : stripTextStyling(rawHtml);
                const segments = splitHtmlByImages(html);
                const activeEditor = editorRef.current;
                if (activeEditor && hasImageSegment(segments)) {
                    event.preventDefault();
                    const { from, to } = view.state.selection;
                    void importPastedImages(
                        activeEditor,
                        segments,
                        { from, to },
                        (file) => uploadOrThrow(onImageUploadRef.current, file),
                        setIsUploading,
                        flashNotice
                    );
                    return true;
                }
                return false;
            },
            handleDrop: (view, event) => {
                if (!imagesEnabledRef.current) return false;
                const files = event.dataTransfer?.files;
                if (!files || files.length === 0) return false;
                const imageFiles = Array.from(files).filter((f) =>
                    (ALLOWED_IMAGE_MIME as readonly string[]).includes(f.type)
                );
                if (imageFiles.length === 0) return false;
                event.preventDefault();
                void uploadAndInsertImageGrid(
                    view,
                    imageFiles,
                    (file) => uploadOrThrow(onImageUploadRef.current, file),
                    setUploadError,
                    setIsUploading
                );
                return true;
            }
        }
    });

    useEffect(() => {
        imagesEnabledRef.current = imagesEnabled;
        textStylingRef.current = allowTextStyling;
    }, [imagesEnabled, allowTextStyling]);

    useEffect(() => {
        onImageUploadRef.current = onImageUpload;
        if (editor) {
            const storage = editor.storage as unknown as Record<string, unknown>;
            storage.onImageUpload = onImageUpload;
            storage.enableImageCaptions = enableImageCaptions;
        }
    }, [onImageUpload, editor, enableImageCaptions]);

    useEffect(() => {
        if (editor) editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    useEffect(() => {
        if (!readOnly && autoFocus) focusAtEnd(editor);
    }, [editor, readOnly, autoFocus]);
    useEffect(() => {
        // useEditor's 1 ms unmounted-destroy timer can beat a deferred effect flush (WebKit);
        // the hook then re-renders with a fresh editor, which this effect syncs instead.
        if (!editor || editor.isDestroyed) return;
        const next = withBlockContent(value, !readOnly);
        if (holdsSameDoc(editor, next)) return;
        editor.commands.setContent(next as unknown as JSONContent, { emitUpdate: false });
        if (!readOnly && autoFocus) focusAtEnd(editor);
    }, [editor, value, readOnly, autoFocus]);

    if (!editor) {
        if (chrome === 'bare') {
            return null;
        }
        if (chrome === 'flush') {
            return (
                <div
                    className="flex-1 min-h-0 bg-white"
                    style={minHeightPx !== undefined ? { minHeight: minHeightPx } : undefined}
                />
            );
        }
        return (
            <div
                className="rounded-lg border border-gray-200 bg-white"
                style={{ minHeight: minHeightPx ?? 160 }}
            />
        );
    }

    const presetCards =
        showPresets && presets ? (
            <PresetCards
                presets={presets}
                onPick={applyPreset}
                onSkip={() => {
                    setPresetsDismissed(true);
                    focusAtEnd(editor);
                }}
            />
        ) : null;

    const uploadStatusBar =
        uploadError || isUploading || notice ? (
            <div
                className={
                    chrome === 'flush'
                        ? 'border-t border-gray-100 px-3 py-1.5 text-[11px] flex items-center gap-2 flex-shrink-0'
                        : 'border-t border-gray-100 px-3 py-1.5 text-[11px] flex items-center gap-2'
                }
            >
                {isUploading && (
                    <span className="inline-flex items-center gap-1.5 text-gray-500">
                        <span className="h-2.5 w-2.5 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
                        Uploading…
                    </span>
                )}
                {notice && !uploadError && (
                    <span className="text-gray-500" role="status">
                        {notice}
                    </span>
                )}
                {uploadError && (
                    <span className="text-red-500" role="alert">
                        {uploadError}
                    </span>
                )}
                {uploadError && (
                    <button
                        type="button"
                        onClick={() => setUploadError(null)}
                        className="ml-auto text-gray-400 hover:text-gray-600"
                    >
                        Dismiss
                    </button>
                )}
            </div>
        ) : null;

    // Read-only hosts wrap the editor in overflow containers that would clip a
    // `fixed inset-0` overlay, so the lightbox goes to the body.
    // `relative z-[100]` is load-bearing: ImageLightbox is `z-50` and so is
    // every modal overlay here, so a lightbox opened from inside a modal would
    // otherwise tie on z-index and lose or win by DOM order alone.
    const lightbox = lightboxImage
        ? createPortal(
              <div className="relative z-[100]">
                  <ImageLightbox
                      src={lightboxImage.src}
                      alt={lightboxImage.alt}
                      onClose={closeLightbox}
                  />
              </div>,
              document.body
          )
        : null;

    const bodyClass = readOnly ? `rich-editor ${RICH_BODY_ZOOMABLE_IMAGE_CLASS}` : 'rich-editor';

    const confirmModal = pendingPreset ? (
        <PresetReplaceConfirmModal
            preset={pendingPreset}
            onCancel={() => setPendingPreset(null)}
            onConfirm={handleConfirmReplace}
        />
    ) : null;

    if (chrome === 'bare') {
        return (
            <>
                <div
                    className={bodyClass}
                    onClick={readOnly ? handleReadOnlyImageClick : undefined}
                >
                    <EditorContent editor={editor} />
                </div>
                {lightbox}
            </>
        );
    }

    if (chrome === 'flush') {
        return (
            <>
                <div className="flex-1 min-h-0 flex flex-col bg-white">
                    {!readOnly && (
                        <Toolbar
                            editor={editor}
                            presets={presets}
                            onApplyPreset={applyPreset}
                            rightSlot={toolbarRightSlot}
                            allowImages={imagesEnabled}
                            allowCallouts={allowCallouts}
                            allowLayoutBlocks={allowLayoutBlocks}
                            allowTextStyling={allowTextStyling}
                        />
                    )}
                    {!readOnly && belowToolbarSlot}
                    {/* The editable text is only as tall as its content, but the pane
             fills the step. Clicking the empty area below the text focuses the
             editor so the whole pane is a "click to type" target. */}
                    <div
                        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden cursor-text"
                        style={minHeightPx !== undefined ? { minHeight: minHeightPx } : undefined}
                        onMouseDown={(e) => {
                            if (!readOnly && e.target === e.currentTarget) {
                                e.preventDefault();
                                focusAtEnd(editor);
                            }
                        }}
                    >
                        {presetCards}
                        <div
                            className={bodyClass}
                            onClick={readOnly ? handleReadOnlyImageClick : undefined}
                        >
                            <EditorContent editor={editor} />
                        </div>
                    </div>
                    {uploadStatusBar}
                </div>
                {confirmModal}
                {lightbox}
            </>
        );
    }

    return (
        <>
            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                {!readOnly && (
                    <Toolbar
                        editor={editor}
                        presets={presets}
                        onApplyPreset={applyPreset}
                        rightSlot={toolbarRightSlot}
                        allowImages={imagesEnabled}
                        allowCallouts={allowCallouts}
                        allowLayoutBlocks={allowLayoutBlocks}
                        allowTextStyling={allowTextStyling}
                    />
                )}
                {!readOnly && belowToolbarSlot}
                <div style={{ minHeight: minHeightPx ?? 160 }}>
                    {presetCards}
                    <div
                        className={bodyClass}
                        onClick={readOnly ? handleReadOnlyImageClick : undefined}
                    >
                        <EditorContent editor={editor} />
                    </div>
                </div>
                {uploadStatusBar}
            </div>
            {confirmModal}
            {lightbox}
        </>
    );
}
